// ============================================
// UPDATED: SimpleScrollList.jsx
// Enhanced with rolling window focus preservation
// PERFORMANCE OPTIMIZED: Per-item memoization instead of whole-list memoization
// FIXED: Now reacts to isShiftingWindow changes to unlock scroll
// ============================================
//
// ARCHITECTURE NOTES:
// - This is the DEFAULT vertical list used by TourParticipant
// - Uses browser native scrolling (overflow-y: auto)
// - Does NOT use VirtualizedGrid (that's only for SmoothScrollList with >100 items)
// - Works with SmartEventList rolling window (maintains ~15 items at a time)
//
// PERFORMANCE OPTIMIZATIONS IMPLEMENTED:
// ✅ Throttled scroll detection (100ms = 10fps)
// ✅ Debounced parent notifications (200ms after scroll settles)
// ✅ requestAnimationFrame for DOM measurements to avoid layout thrashing
// ✅ Duplicate index filtering to prevent unnecessary parent re-renders
// ✅ Guards to skip work during programmatic scroll adjustments
// ✅ Passive event listeners for smooth scrolling on mobile
// ✅ Per-item memoization (not whole-list) for better re-render behavior
//
// ============================================

import React, { useState, useEffect, useLayoutEffect, createRef, useRef, useCallback } from 'react';
import { CircularProgress, Grid, Box, Typography } from '@mui/material';
import PlaceDetails from '../PlaceDetails/PlaceDetails';
import { styled } from '@mui/system';
import axios from 'axios';

// ============================================
// PERFORMANCE UTILITIES
// ============================================

/**
 * Inline debounce utility - delays function execution until after wait time has elapsed
 * since the last call. Used to reduce parent component re-renders.
 */
function debounce(fn, delay) {
  let timer;
  return function(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Inline throttle utility - ensures function runs at most once per limit period.
 * Used to reduce scroll handler frequency.
 */
function throttle(fn, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}


// ============================================
// MEMOIZED ITEM COMPONENT
// ============================================

/**
 * Memoized vertical event row - only re-renders when its props change
 * This is key to maintaining performance after removing whole-list memoization
 */
const VerticalEventRow = React.memo(function VerticalEventRow({
  place,
  index,
  isActive,
  elRef,
  onClick,
  language,
  onLocationClick,
  suppressAutoScroll,
  isShiftingWindow,
  onExpand,
  onCreatorClick,
}) {
  if (place?.__skeleton) {
    return (
      <Grid
        ref={elRef}
        key={`skeleton-${index}`}
        item
        xs={12}
      >
        <div style={{
          width: '100%',
          height: 96,
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,.12)',
          background: 'linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 50%, #f2f2f2 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite'
        }} />
      </Grid>
    );
  }

  return (
    <Grid
      ref={elRef}
      key={place.event_id || place.id || `place-${index}`}
      item
      xs={12}
      data-event-id={place.id || place.event_id}
      onClick={() => onClick(place, index)}
    >
      <PlaceDetails
        place={place}
        selected={isActive}
        refProp={elRef}
        onLocationClick={onLocationClick}
        language={language}
        suppressAutoScroll={suppressAutoScroll}
        isShiftingWindow={isShiftingWindow}
        onExpand={onExpand}
        hideParticipation={true}
        onCreatorClick={onCreatorClick}
      />
    </Grid>
  );
});

const StyledContainer = styled(Box)({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 5px 15px 15px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  position: 'relative',
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
});

const ScrollableGrid = styled(Grid)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingRight: '2px',

  // INSTAGRAM-STYLE: Mobile scroll optimizations
  WebkitOverflowScrolling: 'touch', // iOS momentum scrolling
  overscrollBehavior: 'contain', // Prevent overscroll bounce & scroll chaining

  // INSTAGRAM-STYLE: Enable browser-native scroll anchoring
  // This helps maintain scroll position when content changes above viewport
  overflowAnchor: 'auto', // Let browser help with position preservation

  // GPU acceleration for smooth scrolling
  transform: 'translateZ(0)',
  willChange: 'scroll-position',

  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(0,0,0,0.4)',
  },
}));

const emptyMessages = {
  TH: 'ไม่พบกิจกรรม',
  EN: 'No events found matching your criteria.',
  CN: '未找到符合条件的活动'
};

// Time window to clamp focus after a shift (ms)
const POST_SHIFT_CLAMP_MS = 500; // Extended to ensure we don't miss the window

// Minimum accuracy threshold for scroll compensation
const MIN_COMPENSATION_ACCURACY = 0.85; // tweakable

const SimpleScrollList = ({
  places,
  childClicked,
  isLoading,
  onLocationClick,
  language = 'TH',
  onActiveIndexChange,
  hasSearched = true,
  currentUser = null,
  tracker = null,
  listRef: externalListRef = null,
  pendingScrollAdjustment = null,
  isShiftingWindow = false, // NEW: Prevent focus detection during window shifts
  isLoadingNext = false, // NEW: Prevent focus during next loading
  isLoadingPrevious = false, // NEW: Prevent focus during previous loading
  onScrollStart = null, // PERFORMANCE: Callback when user starts scrolling
  onScrollEnd = null, // PERFORMANCE: Callback when user stops scrolling
  preserveScrollOnPrepend = true, // NEW: Controls automatic scroll compensation after prepend
  // NEW: Authoritative focus anchoring
  authoritativeFocusIndex = null,
  authoritativeFocusEventId = null,
  // NEW: Edge-demand channel - separate from focus for reliable loading
  onEdgeDemand = null,
  // NEW: Post-shift anchor - clamps focus for a short time after shift
  postShiftAnchorIndex = null,
  postShiftAnchorEventId = null,
  postShiftAnchorAt = null,
  postShiftAnchorAuthoritative = false, // NEW: Whether anchor is strong (vs weak fallback)
  // STABILIZATION: Suppress child auto-scroll during window shifts
  suppressAutoScroll = false,
  // NEW: Post-shift silence window duration (ms) - prevents scroll-based focus override after shift
  postShiftSuppressMs = 500,
  // NEW: Force authoritative anchor from parent (Part 2)
  forceAuthoritativeAnchor = false,
  // Expand callback — passed down to PlaceDetails
  onExpand = null,
  onCreatorClick = null,
}) => {
  // Debug log to confirm re-renders
  console.log('[SimpleScrollList] render. isShiftingWindow =', isShiftingWindow);

  const [elRefs, setElRefs] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const scrollContainerRef = useRef(null);
  const isScrollingFromClickRef = useRef(false);
  const lastChildClickedRef = useRef(null);
  const lastReportedIndexRef = useRef(-1);
  const itemHeightCacheRef = useRef(new Map()); // eventId -> measured height
  const lastMeasurementTimeRef = useRef(0); // Throttle measurements
  const previousItemsRef = useRef([]); // Track previous items to detect what was removed
  const scrollAdjustmentInProgressRef = useRef(false);

  const lastScrollTopRef = useRef(0);
  const lastAppliedAdjustmentIdRef = useRef(null);
  const lastHeightUpdateRef = useRef(0);
  const windowShiftCooldownRef = useRef(false);
  const userScrollingRef = useRef(false);
  const dwellTimerRef = useRef(null);
  const lastItemsSignatureRef = useRef(''); // Track items signature to avoid unnecessary measurements
  const lastEdgeDemandTimeRef = useRef(0); // Cooldown for edge demand to prevent spam

  // INSTAGRAM-STYLE: Scroll velocity tracking for dynamic prefetching
  const scrollVelocityRef = useRef(0); // pixels per millisecond
  const lastScrollTimeRef = useRef(0); // timestamp of last scroll event

  useEffect(() => {
    return () => {
      if (tracker) {
        tracker.trackSessionEnd('scroll_list_unmount');
      }
      if (dwellTimerRef.current) clearTimeout(dwellTimerRef.current);
    };
  }, [tracker]);


  // Create refs for all items
  useEffect(() => {
    const placesCount = Array.isArray(places) ? places.length : 0;
    setElRefs((refs) => {
      if (refs.length === placesCount) return refs;
      return Array(placesCount).fill().map((_, i) => refs[i] || createRef());
    });
  }, [places?.length]);

  // Track previous items for compensation
  useEffect(() => {
    if (Array.isArray(places) && places.length > 0) {
      previousItemsRef.current = places;
    }
  }, [places]);

  /**
   * CRITICAL FIX: SYNCHRONOUS height measurement before DOM updates
   * Runs in useLayoutEffect to measure BEFORE items are removed
   * This prevents the repositioning bug where removed items have no cached height
   */
  useLayoutEffect(() => {
    if (!Array.isArray(places) || places.length === 0) return;
    if (!scrollContainerRef.current) return;

    // SYNCHRONOUS measurement - run immediately before DOM updates
    places.forEach((place, index) => {
      if (place?.__skeleton) return;

      const eventId = String(place.event_id || place.id);
      if (!eventId || itemHeightCacheRef.current.has(eventId)) return;

      const ref = elRefs[index];
      if (ref?.current) {
        const rect = ref.current.getBoundingClientRect();
        if (rect.height > 0) {
          itemHeightCacheRef.current.set(eventId, rect.height);
        }
      }
    });
  }, [places, elRefs]);

  /**
   * Track window shift for scroll detection (but don't lock scroll)
   * CRITICAL FIX: Removed aggressive scroll locking that was causing stuck scrolling
   * The scroll adjustment flag is sufficient to prevent interference
   */
  useEffect(() => {
    if (isShiftingWindow) {
      console.log('[SimpleScrollList] 🔄 Window shifting detected (scroll remains enabled)');
      scrollAdjustmentInProgressRef.current = true;

      // INSTAGRAM-STYLE: Very short delay - minimize "dead zones"
      const clearTimer = setTimeout(() => {
        scrollAdjustmentInProgressRef.current = false;
        console.log('[SimpleScrollList] ✅ Scroll adjustment flag cleared');
      }, 30); // Reduced from 300ms to 30ms

      return () => clearTimeout(clearTimer);
    }
  }, [isShiftingWindow]);


  /**
   * SIMPLIFIED: Scroll compensation with height map
   * Always compensates for removeTop using cached heights
   */
  useLayoutEffect(() => {
    if (!pendingScrollAdjustment || !scrollContainerRef.current) {
      return;
    }

    // Prevent duplicate applications
    if (lastAppliedAdjustmentIdRef.current === pendingScrollAdjustment.id) {
      console.log('[SimpleScrollList] ⏭️  Skipping duplicate adjustment ID:', pendingScrollAdjustment.id);
      return;
    }

    const container = scrollContainerRef.current;
    const { kind, id, removedCount, prependedCount } = pendingScrollAdjustment;

    if (kind === 'removeTop') {
      // Always compensate - don't try to be smart
      console.log('[SimpleScrollList] 🔧 REMOVE_TOP: compensating for', removedCount, 'items');

      // Find the removed items from previousItemsRef
      const removedItems = previousItemsRef.current.slice(0, removedCount);
      console.log('[SimpleScrollList] 📦 Removed items:', removedItems.map(i => i.event_id || i.id));

      // Calculate average height from cache for FALLBACK when cache misses
      const cachedHeights = Array.from(itemHeightCacheRef.current.values());
      const averageHeight = cachedHeights.length > 0
        ? cachedHeights.reduce((a, b) => a + b, 0) / cachedHeights.length
        : 150; // Fallback to 150px if no cache at all

      console.log('[SimpleScrollList] 📊 Average height:', Math.round(averageHeight), 'px from', cachedHeights.length, 'cached items');

      // Sum heights from height map with fallback to average
      let delta = 0;
      let cacheMisses = 0;
      removedItems.forEach((item) => {
        const eventId = String(item.event_id || item.id);
        const isCached = itemHeightCacheRef.current.has(eventId);
        const height = isCached ? itemHeightCacheRef.current.get(eventId) : averageHeight;

        if (!isCached) {
          cacheMisses++;
        }

        delta += height;
        console.log('[SimpleScrollList] 📏', eventId, '→',
          isCached ? 'CACHED' : '⚠️ FALLBACK',
          Math.round(height), 'px');
      });

      if (cacheMisses > 0) {
        console.log('[SimpleScrollList] ⚠️ Cache misses:', cacheMisses, '/', removedItems.length, '- using average height fallback');
      }

      if (delta > 0) {
        const oldScrollTop = container.scrollTop;
        const newScrollTop = Math.max(0, oldScrollTop - delta);

        // Mark as programmatic scroll
        scrollAdjustmentInProgressRef.current = true;

        container.scrollTop = newScrollTop;
        console.log('[SimpleScrollList] ✅ Compensated:', Math.round(oldScrollTop), '→', Math.round(newScrollTop), '(delta:', Math.round(delta), 'px)');

        // INSTAGRAM-STYLE: Re-enable immediately after compensation
        setTimeout(() => {
          scrollAdjustmentInProgressRef.current = false;
        }, 30); // Reduced from 100ms to 30ms
      } else {
        // No compensation needed, clear flag immediately
        scrollAdjustmentInProgressRef.current = false;
      }

      lastAppliedAdjustmentIdRef.current = id;
    } else if (kind === 'prepend') {
      // Items added at top → scroll down to compensate
      console.log('[SimpleScrollList] 🔧 PREPEND: compensating for', prependedCount, 'items');

      // Find the prepended items (they're now at the start of places)
      const prependedItems = places.slice(0, prependedCount);
      console.log('[SimpleScrollList] 📦 Prepended items:', prependedItems.map(i => i.event_id || i.id));

      // Sum heights from height map
      let delta = 0;
      prependedItems.forEach((item) => {
        const eventId = String(item.event_id || item.id);
        const height = itemHeightCacheRef.current.get(eventId) || 0;
        delta += height;
      });

      if (delta > 0) {
        const oldScrollTop = container.scrollTop;
        const newScrollTop = oldScrollTop + delta;

        scrollAdjustmentInProgressRef.current = true;
        container.scrollTop = newScrollTop;
        console.log('[SimpleScrollList] ✅ Compensated:', Math.round(oldScrollTop), '→', Math.round(newScrollTop), '(delta:', Math.round(delta), 'px)');

        // INSTAGRAM-STYLE: Re-enable immediately after compensation
        setTimeout(() => {
          scrollAdjustmentInProgressRef.current = false;
        }, 30); // Reduced from 100ms to 30ms
      } else {
        // No compensation needed, clear flag immediately
        scrollAdjustmentInProgressRef.current = false;
      }

      lastAppliedAdjustmentIdRef.current = id;
    }

    // Tell parent we're done - use the callback from adjustment object
    if (typeof pendingScrollAdjustment.onApplied === 'function') {
      pendingScrollAdjustment.onApplied(id);
      console.log('[SimpleScrollList] ✅ Scroll compensation callback fired for adjustment', id);
    }
  }, [pendingScrollAdjustment, places]);

  // Cleanup cache on unmount
  useEffect(() => {
    return () => {
      itemHeightCacheRef.current.clear();
      console.log('[SimpleScrollList] 🧹 Cleaned up height cache');
    };
  }, []);

  /**
   * PERFORMANCE: Debounced parent notification
   * We separate "detecting the centered item" from "notifying the parent"
   * to avoid triggering expensive re-renders on every scroll frame
   */
  const notifyParentIndexChange = useRef(
    debounce((index) => {
      // Only notify if index actually changed from last reported value
      if (index !== lastReportedIndexRef.current && onActiveIndexChange) {
        onActiveIndexChange(index);
        lastReportedIndexRef.current = index;
      }
    }, 200) // 200ms debounce - waits for scrolling to settle
  ).current;

  const incrementItemViewCount = useCallback(async (item) => {
    const isReview = item._source === 'review';
    const id = parseInt(item.id, 10);
    const type = isReview ? 'review' : 'event';
    if (!id || isNaN(id)) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/views/increment`, { type, id });
    } catch (err) {
      console.warn('[ViewCount] increment failed:', err.message);
    }
  }, []);

  /**
   * Find centered card - simplified version
   * PERFORMANCE: Uses requestAnimationFrame + debounced parent notification
   * CRITICAL: Includes edge detection to trigger loading more items
   */
  const findCenteredCard = useCallback(() => {
    // Guard: skip if no valid data or currently adjusting scroll
    if (!scrollContainerRef.current || !elRefs.length || !Array.isArray(places)) return;
    if (isScrollingFromClickRef.current || scrollAdjustmentInProgressRef.current) return;

    // Block during window shifts
    if (isShiftingWindow) {
      console.log('[SimpleScrollList] ⏸️  findCenteredCard blocked during window shift');
      return;
    }

    // Use requestAnimationFrame to batch DOM reads
    requestAnimationFrame(() => {
      if (!scrollContainerRef.current || !elRefs.length) return;

      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;

      let closestIndex = 0;
      let minDistance = Infinity;

      // Find the centered card
      elRefs.forEach((ref, index) => {
        if (ref?.current && places[index] && !places[index].__skeleton) {
          const elementRect = ref.current.getBoundingClientRect();
          const elementCenter = elementRect.top + elementRect.height / 2;
          const distance = Math.abs(elementCenter - containerCenter);

          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        }
      });

      // INSTAGRAM-STYLE EDGE DETECTION: Aggressive loading for seamless scroll
      const validPlacesCount = places.filter(p => !p.__skeleton).length;
      const threshold = 7; // Trigger when within 7 items from edges (very aggressive)

      // INSTAGRAM-STYLE: Short cooldown for rapid loading
      const now = Date.now();
      const cooldownPassed = now - lastEdgeDemandTimeRef.current > 300; // 300ms - fast response

      // Near bottom - load next items (prioritize downward scroll)
      if (closestIndex >= validPlacesCount - threshold && onEdgeDemand && cooldownPassed) {
        console.log('[SimpleScrollList] 📍 Instagram-style edge at index', closestIndex, '/', validPlacesCount, '- loading more');
        onEdgeDemand('down');
        lastEdgeDemandTimeRef.current = now;
      }

      // Near top - load previous items
      if (closestIndex <= threshold && onEdgeDemand && cooldownPassed) {
        console.log('[SimpleScrollList] 📍 Instagram-style edge at index', closestIndex, '/', validPlacesCount, '- loading previous');
        onEdgeDemand('up');
        lastEdgeDemandTimeRef.current = now;
      }

      // Update if changed
      const hasChanged = closestIndex !== activeIndex &&
                        places[closestIndex] &&
                        !places[closestIndex].__skeleton;

      if (hasChanged) {
        setActiveIndex(closestIndex);
        const currentItem = places[closestIndex];

        // Report to parent
        if (onActiveIndexChange && currentItem && !currentItem.__skeleton) {
          onActiveIndexChange(closestIndex, currentItem);
        }

        // Start 3-second dwell timer for the newly centered item
        if (dwellTimerRef.current) {
          clearTimeout(dwellTimerRef.current);
          dwellTimerRef.current = null;
        }
        const centeredItem = places[closestIndex];
        if (centeredItem && !centeredItem.__skeleton) {
          dwellTimerRef.current = setTimeout(() => {
            dwellTimerRef.current = null;
            incrementItemViewCount(centeredItem);
          }, 3000);
        }
      }
    });
  }, [elRefs, places, activeIndex, tracker, onActiveIndexChange, isShiftingWindow, incrementItemViewCount, onEdgeDemand]);

  /**
   * PERFORMANCE: Throttled scroll handler
   * Tracks user scrolling state and notifies parent when scrolling starts/stops
   * OPTIMIZED: Reduced guards for smoother scrolling on mobile
   * CRITICAL: Includes scroll position-based edge detection
   */
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    // CRITICAL: Only ignore during actual compensation, not during all shifts
    // This allows smoother scrolling by reducing blocked scroll events
    if (scrollAdjustmentInProgressRef.current) {
      console.log('[SimpleScrollList] ⏸️  Ignoring scroll during compensation');
      return;
    }

    const container = scrollContainerRef.current;
    const scrollTop = container.scrollTop;

    // INSTAGRAM-STYLE: Calculate scroll velocity for dynamic prefetching
    const now = Date.now();
    const scrollDelta = Math.abs(scrollTop - lastScrollTopRef.current);
    const timeDelta = now - lastScrollTimeRef.current;

    if (timeDelta > 0) {
      // Velocity in pixels per millisecond
      scrollVelocityRef.current = scrollDelta / timeDelta;
    }

    lastScrollTopRef.current = scrollTop;
    lastScrollTimeRef.current = now;

    // Notify parent that scrolling has started
    if (onScrollStart) {
      onScrollStart();
    }

    // INSTAGRAM-STYLE POSITION-BASED DETECTION with velocity-based prefetching
    // Fast scroll = load earlier (3x threshold), slow scroll = normal threshold
    if (onEdgeDemand && !isShiftingWindow) {
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const scrollBottom = scrollHeight - (scrollTop + clientHeight);

      // Dynamic threshold based on scroll velocity
      // Fast scroll (> 1px/ms) = 3x threshold, slow/medium = 1-2x threshold
      const velocityMultiplier = Math.min(3, Math.max(1, 1 + scrollVelocityRef.current * 2));
      const baseThreshold = 600;
      const edgeThreshold = Math.round(baseThreshold * velocityMultiplier);

      const cooldownPassed = now - lastEdgeDemandTimeRef.current > 300;

      // Near bottom - load more items with velocity-based prefetching
      if (scrollBottom < edgeThreshold && cooldownPassed) {
        console.log('[SimpleScrollList] 📍 Instagram-style position trigger:',
          Math.round(scrollBottom), 'px from bottom,',
          'velocity:', scrollVelocityRef.current.toFixed(2), 'px/ms,',
          'threshold:', Math.round(edgeThreshold), 'px',
          '(' + velocityMultiplier.toFixed(1) + 'x)');
        onEdgeDemand('down');
        lastEdgeDemandTimeRef.current = now;
      }

      // Near top - load previous items with velocity-based prefetching
      if (scrollTop < edgeThreshold && cooldownPassed) {
        console.log('[SimpleScrollList] 📍 Instagram-style position trigger:',
          Math.round(scrollTop), 'px from top,',
          'velocity:', scrollVelocityRef.current.toFixed(2), 'px/ms,',
          'threshold:', Math.round(edgeThreshold), 'px');
        onEdgeDemand('up');
        lastEdgeDemandTimeRef.current = now;
      }
    }

    // Throttled: find which card is centered
    // Only skip if actively shifting (not just loading)
    if (!isShiftingWindow) {
      findCenteredCard();
    }

    // Clear user scrolling flag after inactivity
    clearTimeout(window._scrollInactivityTimer);
    window._scrollInactivityTimer = setTimeout(() => {
      if (onScrollEnd) {
        onScrollEnd();
      }
    }, 200);
  }, [findCenteredCard, onScrollStart, onScrollEnd, isShiftingWindow, onEdgeDemand]);

  /**
   * PERFORMANCE: Set up scroll listeners with throttling
   * We use 100ms throttle (10fps) for better performance on mobile
   * Plus a debounced "scroll end" handler for final accuracy
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !onActiveIndexChange) return;

    // Throttled scroll handler (100ms = 10fps, good balance for mobile)
    const throttledHandler = throttle(handleScroll, 100);

    // Debounced scroll end handler for final accuracy (150ms after last scroll)
    let scrollEndTimeout;
    const scrollEndHandler = () => {
      clearTimeout(scrollEndTimeout);
      scrollEndTimeout = setTimeout(() => {
        handleScroll();
      }, 150);
    };

    container.addEventListener('scroll', throttledHandler, { passive: true });
    container.addEventListener('scroll', scrollEndHandler, { passive: true });

    return () => {
      container.removeEventListener('scroll', throttledHandler);
      container.removeEventListener('scroll', scrollEndHandler);
      clearTimeout(scrollEndTimeout);
      if (window._scrollInactivityTimer) {
        clearTimeout(window._scrollInactivityTimer);
      }
    };
  }, [handleScroll, onActiveIndexChange]);

  /**
   * Scroll to item when clicked from map
   * ENHANCED: Respects window shifting + cooldown + user scrolling + all loading states
   * CRITICAL: Ignores self-originated selection to prevent feedback loop
   */
  useEffect(() => {
    if (childClicked === null ||
        childClicked === lastChildClickedRef.current ||
        isScrollingFromClickRef.current ||
        scrollAdjustmentInProgressRef.current ||
        isShiftingWindow || // ← BLOCK during window shifts
        windowShiftCooldownRef.current || // ← BLOCK during cooldown
        userScrollingRef.current || // ← BLOCK when user is scrolling
        isLoadingNext || // ← BLOCK during next loading
        isLoadingPrevious || // ← BLOCK during previous loading
        !elRefs[childClicked]?.current) {
      return;
    }

    // CRITICAL: If the parent is just echoing back what we already scrolled to, ignore it
    // This prevents the feedback loop: scroll → onActiveIndexChange → parent updates state →
    // childClicked changes → scrollIntoView → ruins free scrolling
    if (childClicked === activeIndex) {
      console.log('🎯 [SimpleScrollList] Ignoring childClicked - already at activeIndex:', activeIndex);
      return;
    }

    console.log('🎯 [SimpleScrollList] Scrolling to item:', childClicked);

    isScrollingFromClickRef.current = true;
    lastChildClickedRef.current = childClicked;

    requestAnimationFrame(() => {
      if (elRefs[childClicked]?.current) {
        elRefs[childClicked].current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        setTimeout(() => {
          isScrollingFromClickRef.current = false;
        }, 1200);
      } else {
        isScrollingFromClickRef.current = false;
      }
    });
  }, [childClicked, elRefs, isShiftingWindow, isLoadingNext, isLoadingPrevious, activeIndex]);

  /**
   * NOTE: We removed the duplicate isShiftingWindow effect that was unlocking immediately.
   * Lock/unlock is now handled in the main effect (lines 313-361) which properly waits
   * for compensation verification before unlocking.
   */

  /**
   * NOTE: We removed scroll position preservation during shifts
   * The removeTop compensation is sufficient and preserving scroll was fighting it
   */

  const getEmptyMessage = () => {
    return emptyMessages[language] || emptyMessages.EN;
  };

  const validPlaces = Array.isArray(places) ? places : [];

  const handleItemClick = useCallback((place, index) => {
    if (!place.__skeleton && tracker) {
      tracker.trackInteraction({
        eventId: place.id || place.event_id,
        action: 'click_event_card',
        context: 'scroll_list',
        position: index,
        timestamp: Date.now(),
      });
    }
  }, [tracker]);

  return (
    <StyledContainer>
      {isLoading ? (
        <LoadingContainer>
          <CircularProgress size={50} />
        </LoadingContainer>
      ) : (
        <ScrollableGrid
          ref={scrollContainerRef}
          container
          spacing={0}
          sx={{
            // MOBILE OPTIMIZATIONS: Better touch handling
            touchAction: 'pan-y',
            scrollBehavior: 'smooth',
            // Reduce overscroll bounce on iOS
            overscrollBehavior: 'contain',
            // Keep overflow: auto to allow scroll detection, but prevent visual jumps via scroll restoration
          }}
        >
          {validPlaces.length > 0 ? (
            validPlaces.map((place, i) => (
              <VerticalEventRow
                key={place.event_id || place.id || `place-${i}`}
                place={place}
                index={i}
                isActive={Number(childClicked) === i}
                elRef={elRefs[i]}
                onClick={handleItemClick}
                language={language}
                onLocationClick={onLocationClick}
                suppressAutoScroll={suppressAutoScroll}
                isShiftingWindow={isShiftingWindow}
                onExpand={onExpand}
                onCreatorClick={onCreatorClick}
              />
            ))
          ) : (
            <Box sx={{ width: '100%', textAlign: 'center', padding: 2 }}>
              <Typography>
                {hasSearched ? getEmptyMessage() : (
                  language === 'TH' ? 'กำลังโหลด...' :
                  language === 'CN' ? '加载中...' :
                  'Loading...'
                )}
              </Typography>
            </Box>
          )}
        </ScrollableGrid>
      )}

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </StyledContainer>
  );
};

// STEP 1: Remove outer React.memo - export component directly
// This allows the component to re-render when isShiftingWindow changes
export default SimpleScrollList;